import { Injectable, inject } from '@angular/core';
import { tableToIPC } from 'apache-arrow';
import { GeneralHelpers } from '../../helpers/general.helper';
import { Papa } from 'ngx-papaparse';
import { fromCSV, toArrow } from 'arquero';
import { ExecutionContext } from '../../interfaces/chunk/chunk-context.interface';

@Injectable({
  providedIn: 'root',
})
export class ConvertService {
  private readonly papa = inject(Papa);


  public async csvToArrow(
    args: any[],
    chunkContext: ExecutionContext
  ): Promise<Uint8Array> {
    // chunkContext.setBusy(true, 'Converting CSV to Arrow');
    const csvData = args[0];

    if (!csvData) {
      const error = new Error('CSV data cannot be null or undefined.');
      chunkContext.addMessage('CSV data cannot be null or undefined.', 'danger');
      // chunkContext.setBusy(false);
      throw error;
    }

    try {
      let csvString = '';

      // Handle structured data (array of objects) - convert to CSV
      if (Array.isArray(csvData) && csvData.length > 0 && typeof csvData[0] === 'object') {
        csvString = this.papa.unparse(csvData);
      }
      // Handle binary data (Uint8Array/ArrayBuffer/byte string) - decode to CSV text
      else if (csvData instanceof Uint8Array || csvData instanceof ArrayBuffer) {
        csvString = new TextDecoder('utf-8').decode(
          csvData instanceof Uint8Array ? csvData : new Uint8Array(csvData)
        );
      }
      // Handle string - could be CSV text or byte string
      else if (typeof csvData === 'string') {
        // If byte string, normalize and decode
        if (GeneralHelpers.canBeParsedToNumberArray(csvData)) {
          const uint8 = GeneralHelpers.normalizeToBinary(csvData);
          csvString = new TextDecoder('utf-8').decode(uint8);
        } else {
          // Already CSV text string
          csvString = csvData;
        }
      }
      else {
        const error = new Error('Invalid CSV data format: must be a string, Uint8Array, ArrayBuffer, or array of objects.');
        chunkContext.addMessage('Invalid CSV data format: must be a string, Uint8Array, ArrayBuffer, or array of objects.', 'danger');
        // chunkContext.setBusy(false);
        throw error;
      }

      const arqueroTable = fromCSV(csvString);
      const arqueroToArrowTable = toArrow(arqueroTable);
      const arrowTable = tableToIPC(arqueroToArrowTable as any);
      // chunkContext.setBusy(false);
      return arrowTable;
    } catch (error: any) {
      console.error(error);
      chunkContext.addMessage('Error processing CSV data: ' + (error.message || error), 'danger');
      // chunkContext.setBusy(false);
      throw error; // Rethrow to allow try/catch blocks to work
    }
  }

  public async jsonToCSV(
    args: any[],
    chunkContext: ExecutionContext
  ): Promise<Uint8Array> {
    // chunkContext.setBusy(true, 'Converting JSON to CSV');
    if (
      !args[0] ||
      (typeof args[0] !== 'object' &&
        !Array.isArray(args[0]) &&
        !GeneralHelpers.isJSON(args[0]))
    ) {
      const error = new Error('Invalid JSON data format: Data must be an object or array.');
      chunkContext.addMessage(
        'Invalid JSON data format: Data must be an object or array.',
        'danger'
      );
      // chunkContext.setBusy(false);
      throw error;
    }

    try {
      const jsonData = args[0];
      const csv = this.papa.unparse(jsonData);
      const result = new TextEncoder().encode(csv);
      // chunkContext.setBusy(false);
      return result;
    } catch (error: any) {
      console.error(error);
      chunkContext.addMessage(
        'Error converting JSON to CSV: ' + (error.message || error),
        'danger'
      );
      // chunkContext.setBusy(false);
      throw error; // Rethrow to allow try/catch blocks to work
    }
  }

  public async csvToJSON(
    args: any[],
    chunkContext: ExecutionContext
  ): Promise<any[]> {
    // chunkContext.setBusy(true, 'Converting CSV to JSON');
    if (!args[0]) {
      const error = new Error('Invalid CSV data: Data cannot be null or undefined.');
      chunkContext.addMessage(
        'Invalid CSV data: Data cannot be null or undefined.',
        'danger'
      );
      // chunkContext.setBusy(false);
      throw error;
    }

    let csvString: string;

    if (typeof args[0] === 'string') {
      if (GeneralHelpers.canBeParsedToNumberArray(args[0])) {
        const bytes = args[0].split(',').map(Number);
        csvString = new TextDecoder('utf-8').decode(new Uint8Array(bytes));
      } else {
        csvString = args[0];
      }
    } else {
      const error = new Error('Invalid CSV data format: Data must be a string or byte string.');
      chunkContext.addMessage(
        'Invalid CSV data format: Data must be a string or byte string.',
        'danger'
      );
      // chunkContext.setBusy(false);
      throw error;
    }

    try {
      const result = this.papa.parse(csvString, { header: true });
      if (result.errors && result.errors.length > 0) {
        const error = new Error('CSV parsing error: ' + result.errors.map((e) => e.message).join('; '));
        chunkContext.addMessage(
          'CSV parsing error: ' +
          result.errors.map((e) => e.message).join('; '),
          'danger'
        );
        // chunkContext.setBusy(false);
        throw error;
      }
      // chunkContext.setBusy(false);
      return result.data;
    } catch (error: any) {
      if (error.message && error.message.includes('CSV parsing error:')) {
        // Error message has already been handled
        throw error;
      }
      console.error(error);
      chunkContext.addMessage(
        'Error converting CSV to JSON: ' + (error.message || error),
        'danger'
      );
      // chunkContext.setBusy(false);
      throw error; // Rethrow to allow try/catch blocks to work
    }
  }
}
